import { useState, useEffect, useMemo, useCallback } from 'react'
import { supabase } from './supabase'

const BROKERS = ['Ben Rogers','Jesse Roach','Mitch Speer']
const MMS_SPLIT = 0.0695
const REFERRAL_SPLIT = 0.4
const FSO_SOURCE = 'Financial Services Online'

const parseCurr = (v) => Number((v||'').toString().replace(/[^\d.]/g,'')) || 0
const fmtMoney = (n) => {
  if (!Number.isFinite(n)) return '$0.00'
  return n.toLocaleString('en-AU',{ style:'currency', currency:'AUD', minimumFractionDigits:2, maximumFractionDigits:2 })
}
const fmtShort = (n) => {
  if (!n || n <= 0) return '$0'
  return n.toLocaleString('en-AU',{ style:'currency', currency:'AUD', minimumFractionDigits:0, maximumFractionDigits:0 })
}
const normName = (s) => (s||'').toLowerCase().replace(/[^a-z\s]/g,'').replace(/\s+/g,' ').trim()

// Derive lead type from the FSO description (e.g. "truck insurance lead - Andrew Dodd" → "Truck & Transport")
const TYPE_MAP = [
  [/truck|transport|prime mover|heavy (motor|vehicle)/i, 'Truck & Transport'],
  [/commercial motor|comm motor/i, 'Commercial Motor'],
  [/comm(ercial)? ?(property|prop)/i, 'Commercial Property'],
  [/plant|equipment/i, 'Plant & Equipment'],
  [/professional indemnity|prof indemnity/i, 'Professional Indemnity'],
  [/public liability|liability/i, 'Liability'],
  [/business (pack|insurance)|biz pack/i, 'Business Pack'],
  [/farm|crop|rural/i, 'Farm & Rural'],
  [/home|contents|landlord/i, 'Home & Property'],
  [/car|motor vehicle|private motor/i, 'Motor'],
]
const leadTypeOf = (description) => {
  const m = (description||'').match(/^(.*?)\s*insurance lead\s*-/i)
  const raw = m ? m[1].trim() : ''
  if (!raw) return 'Other'
  for (const [re, label] of TYPE_MAP) if (re.test(raw)) return label
  return raw.replace(/\b\w/g, c => c.toUpperCase())
}

// Revenue = SAME as Commissions page: (grossComm + brokerFee − MMS) × 1.1, minus referral
function clientNetRevenue(client, coverConfirmations) {
  const covers = (coverConfirmations||[]).filter(cc => cc.client_id === client.id)
  let net = 0
  covers.forEach(cc => {
    const d = cc.cover_data || {}
    const commAmount = parseCurr(d.commission_amount)
    const fee = parseCurr(d.broker_fee)
    const basePrem = parseCurr(d.base_premium)
    const commRate = Number(d.comm_rate||0)
    const grossComm = commAmount > 0 ? commAmount : basePrem * (commRate/100)
    const grossIncome = grossComm + fee
    const arIncome = (grossIncome - grossIncome*MMS_SPLIT) * 1.1
    const hasReferral = !!client.referring_partner && !d.skip_referral
    const referralCost = hasReferral ? (grossComm - grossComm*MMS_SPLIT) * REFERRAL_SPLIT : 0
    net += arIncome - referralCost
  })
  return net
}

// ── Statement parser ──────────────────────────────────────────────────────────
// Handles FSO copy-paste: tab/space separated rows.
// A lead row: DATE  REF  "<type> insurance lead - <Name>"  <blank>  $debit  $balance CR
// A deposit:  DATE  REF  "Account deposit via credit card"  $credit  <blank>  $balance CR
function parseStatement(text) {
  const lines = text.split('\n')
  const rows = []
  // Match a leading date dd-mm-yyyy
  const dateRe = /^\s*(\d{2})-(\d{2})-(\d{4})\b/
  for (const raw of lines) {
    const line = raw.replace(/\u00a0/g,' ').trim()
    if (!line) continue
    const dm = line.match(dateRe)
    if (!dm) continue // skip summary, headers, "Previous Balance" (no date)

    const [, dd, mm, yyyy] = dm
    const isoDate = `${yyyy}-${mm}-${dd}`

    // Pull all dollar amounts in order
    const amounts = (line.match(/\$\s?-?[\d,]+\.\d{2}/g) || []).map(a => parseCurr(a))
    // Reference = first number token after the date
    const afterDate = line.slice(dm[0].length).trim()
    const refMatch = afterDate.match(/^(\d{4,})\b/)
    const reference = refMatch ? refMatch[1] : ''

    // Description = between reference and the first $ amount
    let desc = afterDate
    if (refMatch) desc = desc.slice(refMatch[0].length).trim()
    const firstDollar = desc.search(/\$/)
    if (firstDollar > -1) desc = desc.slice(0, firstDollar).trim()
    desc = desc.replace(/\t/g,' ').replace(/\s+/g,' ').trim()
    if (!desc) continue

    const isLead = /insurance lead\s*-/i.test(desc)
    const isDeposit = /deposit|signup|bonus|credit note/i.test(desc)

    // Last amount on the line is the running balance — ignore it.
    // The transaction amount is the remaining one.
    let credit = 0, debit = 0
    const txnAmounts = amounts.slice(0, Math.max(0, amounts.length - 1)) // drop balance
    const txnAmt = txnAmounts.length ? txnAmounts[txnAmounts.length - 1] : 0

    if (isLead) debit = txnAmt
    else if (isDeposit) credit = txnAmt
    else { // unknown — guess by keyword
      if (/lead/i.test(desc)) debit = txnAmt; else credit = txnAmt
    }

    let leadName = ''
    if (isLead) {
      const nm = desc.match(/insurance lead\s*-\s*(.+)$/i)
      leadName = nm ? nm[1].trim() : ''
    }

    rows.push({
      txn_date: isoDate, reference, description: desc,
      type: isLead ? 'lead' : isDeposit ? 'deposit' : 'other',
      credit, debit, lead_name: leadName,
    })
  }
  return rows
}

export function FSO({ clients, coverConfirmations, openClient, showToast, user }) {
  const [txns, setTxns] = useState([])
  const [loading, setLoading] = useState(true)
  const [pasteText, setPasteText] = useState('')
  const [parsed, setParsed] = useState([])
  const [showImport, setShowImport] = useState(false)
  const [importing, setImporting] = useState(false)

  const fsoClients = useMemo(
    () => (clients||[]).filter(c => c.lead_source === FSO_SOURCE),
    [clients]
  )

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.from('fso_transactions').select('*').order('txn_date',{ascending:true})
    setTxns(data||[])
    setLoading(false)
  }, [])
  useEffect(() => { load() }, [load])

  // ── Totals ──
  const totals = useMemo(() => {
    let deposited = 0, spent = 0, leadCount = 0
    txns.forEach(t => {
      deposited += Number(t.credit||0)
      spent += Number(t.debit||0)
      if (t.type === 'lead') leadCount += 1
    })
    return { deposited, spent, balance: deposited - spent, leadCount }
  }, [txns])

  // ── ROI: lead txns linked to a client → did they convert? what revenue? ──
  const roi = useMemo(() => {
const leadTxns = txns.filter(t => t.type === 'lead')
    let linkedCost = 0, converted = 0, policies = 0, revenue = 0
    leadTxns.forEach(t => {
      if (!t.matched_client_id) return
      const c = (clients||[]).find(cl => cl.id === t.matched_client_id)
      if (!c) return

      
      linkedCost += Number(t.debit||0)
      const placed = c.lead_status === 'Policy Placed' || c.is_client
      if (placed) {
        converted += 1
        policies += (coverConfirmations||[]).filter(cc => cc.client_id === c.id).length
        revenue += clientNetRevenue(c, coverConfirmations)
      }
    })
    const totalLeadSpend = leadTxns.reduce((s,t)=>s+Number(t.debit||0),0)
    return {
      leadCount: leadTxns.length,
      converted,
      policies,
      convRate: leadTxns.length ? Math.round((converted/leadTxns.length)*100) : 0,
      revenue,
      totalLeadSpend,
      net: revenue - totalLeadSpend,
    }
  }, [txns, clients, coverConfirmations])

  // ── ROI grouped by lead type ──
  const byType = useMemo(() => {
    const map = {}
    txns.filter(t => t.type === 'lead').forEach(t => {
      const key = leadTypeOf(t.description)
      if (!map[key]) map[key] = { type: key, count: 0, spend: 0, converted: 0, policies: 0, revenue: 0 }
      map[key].count += 1
      map[key].spend += Number(t.debit||0)
      if (t.matched_client_id) {
const c = (clients||[]).find(cl => cl.id === t.matched_client_id)
        if (c && (c.lead_status === 'Policy Placed' || c.is_client)) {
          map[key].converted += 1
          map[key].policies += (coverConfirmations||[]).filter(cc => cc.client_id === c.id).length
          map[key].revenue += clientNetRevenue(c, coverConfirmations)
        }
      }
    })
    return Object.values(map).map(r => ({ ...r, net: r.revenue - r.spend })).sort((a,b)=>b.net-a.net)
  }, [txns, clients, coverConfirmations])

  // ── Auto-suggest match for a name ──
  const suggestMatch = useCallback((leadName) => {
    if (!leadName) return null
    const target = normName(leadName)
    // exact normalised match first
    let hit = fsoClients.find(c => normName(c.company_name) === target)
    if (hit) return hit
    // surname + first-initial fallback
    const parts = target.split(' ')
    if (parts.length >= 2) {
      const last = parts[parts.length-1]
      const firstInit = parts[0][0]
      hit = fsoClients.find(c => {
        const cn = normName(c.company_name).split(' ')
        return cn.length>=2 && cn[cn.length-1]===last && cn[0][0]===firstInit
      })
    }
    return hit || null
  }, [fsoClients])

  // ── Parse paste ──
  const handleParse = () => {
    const rows = parseStatement(pasteText)
    if (!rows.length) { showToast('No transaction rows found — check the paste'); return }
    setParsed(rows)
  }

  // ── Import parsed rows ──
  const handleImport = async () => {
    setImporting(true)
    const payload = parsed.map(r => ({ ...r, created_by: user?.name || null }))
    // Insert; duplicates (same date+ref+desc) are rejected by unique index → ignore those errors
    let inserted = 0, skipped = 0
    for (const row of payload) {
      const { error } = await supabase.from('fso_transactions').insert(row)
      if (error) { if (error.code === '23505') skipped += 1; else console.error(error) }
      else inserted += 1
    }
    setImporting(false)
    setShowImport(false)
    setPasteText(''); setParsed([])
    await load()
    showToast(`Imported ${inserted} new${skipped ? `, skipped ${skipped} duplicate` : ''}`)
  }

  // ── Link a lead txn to a client ──
  const linkTxn = async (txnId, clientId) => {
    const status = clientId ? 'matched' : 'no_match'
    await supabase.from('fso_transactions').update({ matched_client_id: clientId || null, match_status: status }).eq('id', txnId)
    setTxns(prev => prev.map(t => t.id===txnId ? {...t, matched_client_id: clientId||null, match_status: status} : t))
  }

  const leadTxns = txns.filter(t => t.type === 'lead').sort((a,b)=>new Date(b.txn_date)-new Date(a.txn_date))

  if (loading) return <div className="loading"><div className="spinner"/><span>Loading FSO data...</span></div>

  return (
    <div>
      {/* HERO */}
      <div style={{background:'linear-gradient(135deg,#1e3d30,#2a5240)',borderRadius:16,padding:'24px 28px',marginBottom:20,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div>
          <div style={{fontSize:11,fontWeight:600,color:'rgba(255,255,255,0.5)',textTransform:'uppercase',letterSpacing:'.8px',marginBottom:6}}>Financial Services Online — Balance</div>
          <div style={{fontSize:42,fontWeight:700,color: totals.balance < 100 ? '#f26522' : '#fff',lineHeight:1,letterSpacing:'-1px'}}>{fmtShort(totals.balance)}</div>
          <div style={{fontSize:13,color:'rgba(255,255,255,0.45)',marginTop:10}}>
            {fmtShort(totals.deposited)} deposited · {fmtShort(totals.spent)} spent · {totals.leadCount} leads bought
            {totals.balance < 100 && <span style={{color:'#f26522',fontWeight:700}}> · ⚠️ Low balance</span>}
          </div>
        </div>
        <button className="btn btn-orange" onClick={()=>setShowImport(true)}>+ Import Statement</button>
      </div>

      {/* ROI STRIP */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:20}}>
        {[
          { label:'Leads Bought', value: roi.leadCount, sub: fmtShort(roi.totalLeadSpend)+' spent', color:'var(--text)' },
          { label:'Converted', value: roi.converted, sub: roi.convRate+'% · '+roi.policies+' policies sold', color:'var(--forest)' },
          { label:'Revenue Earned', value: fmtShort(roi.revenue), sub:'net, after MMS', color:'var(--forest)' },
          { label:'Net Profit', value: fmtShort(roi.net), sub:'revenue − spend', color: roi.net >= 0 ? 'var(--forest)' : 'var(--danger)' },
        ].map(card => (
          <div key={card.label} style={{background:'var(--surface)',border:'1.5px solid var(--border)',borderRadius:12,padding:'16px 18px'}}>
            <div style={{fontSize:10,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:'.5px',marginBottom:8}}>{card.label}</div>
            <div style={{fontSize:24,fontWeight:700,color:card.color}}>{card.value}</div>
            <div style={{fontSize:11,color:'var(--muted)',marginTop:3}}>{card.sub}</div>
</div>
        ))}
      </div>

      {/* BY LEAD TYPE */}
      {byType.length > 0 && (
        <div style={{background:'var(--surface)',border:'1.5px solid var(--border)',borderRadius:14,overflow:'hidden',marginBottom:20}}>
          <div style={{padding:'16px 20px',borderBottom:'1px solid var(--border)',fontSize:13,fontWeight:700}}>ROI by Lead Type</div>
          <div className="tbl-header" style={{gridTemplateColumns:'1.6fr 80px 90px 110px 100px 100px'}}>
            <span>Type</span><span>Bought</span><span>Spent</span><span>Converted</span><span>Earned</span><span>Net</span>
          </div>
          {byType.map(r => (
            <div key={r.type} className="tbl-row" style={{gridTemplateColumns:'1.6fr 80px 90px 110px 100px 100px',cursor:'default'}}>
              <div style={{fontSize:13,fontWeight:600}}>{r.type}</div>
              <div style={{fontSize:12}}>{r.count}</div>
              <div style={{fontSize:12,color:'var(--muted)'}}>{fmtShort(r.spend)}</div>
              <div style={{fontSize:12}}>
                {r.converted}<span style={{color:'var(--muted)'}}> · {r.count?Math.round(r.converted/r.count*100):0}%</span>
                {r.converted>0 && (() => {
                  const ratio = r.policies / r.converted
                  const expanded = ratio > 1.0
                  return (
                    <div style={{fontSize:10,color:'var(--muted)',marginTop:2}}>
                      {r.policies} pol · <span style={{fontWeight:expanded?700:400,color:expanded?'var(--forest)':'var(--muted)'}}>×{ratio.toFixed(1)}</span>
                    </div>
                  )
                })()}
              </div>
              <div style={{fontSize:12,fontWeight:600,color:'var(--forest)'}}>{fmtShort(r.revenue)}</div>
              <div style={{fontSize:13,fontWeight:700,color:r.net>=0?'var(--forest)':'var(--danger)'}}>{fmtShort(r.net)}</div>
            </div>
          ))}
        </div>
      )}

      {/* LEADS + MATCHING */}
      <div style={{background:'var(--surface)',border:'1.5px solid var(--border)',borderRadius:14,overflow:'hidden'}}>
        <div style={{padding:'16px 20px',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div style={{fontSize:13,fontWeight:700}}>Leads Purchased ({leadTxns.length})</div>
          <div style={{fontSize:11,color:'var(--muted)'}}>{leadTxns.filter(t=>!t.matched_client_id && t.match_status!=='no_match').length} unmatched</div>
        </div>
        <div className="tbl-header" style={{gridTemplateColumns:'90px 1.4fr 90px 1.6fr 1fr'}}>
          <span>Date</span><span>Lead</span><span>Cost</span><span>Matched Client</span><span>Outcome</span>
        </div>
        {leadTxns.length === 0 ? (
          <div className="empty" style={{padding:'32px 24px'}}><div className="empty-icon">📥</div><div className="empty-title">No leads imported yet</div><div className="empty-sub">Click Import Statement and paste your FSO activity statement</div></div>
        ) : leadTxns.map(t => {
          const matched = t.matched_client_id ? (clients||[]).find(c=>c.id===t.matched_client_id) : null
          const suggestion = !t.matched_client_id && t.match_status!=='no_match' ? suggestMatch(t.lead_name) : null
          const placed = matched && (matched.lead_status==='Policy Placed' || matched.is_client)
          const ni = matched && matched.lead_status === 'Not Interested'
          return (
            <div key={t.id} className="tbl-row" style={{gridTemplateColumns:'90px 1.4fr 90px 1.6fr 1fr',cursor:'default'}}>
              <div style={{fontSize:12,color:'var(--muted)'}}>{new Date(t.txn_date).toLocaleDateString('en-AU',{day:'numeric',month:'short'})}</div>
              <div style={{fontSize:13,fontWeight:600}}>{t.lead_name||t.description}</div>
              <div style={{fontSize:12,fontWeight:600}}>{fmtMoney(t.debit)}</div>
              <div onClick={e=>e.stopPropagation()}>
                <select
                  className="form-select"
                  style={{fontSize:12,padding:'5px 8px',height:30}}
                  value={t.matched_client_id || (t.match_status==='no_match' ? '__none__' : (suggestion ? '__suggest__' : ''))}
                  onChange={e => {
                    const v = e.target.value
                    if (v === '__suggest__') linkTxn(t.id, suggestion.id)
                    else if (v === '__none__') linkTxn(t.id, null)
                    else linkTxn(t.id, v || null)
                  }}
                >
                  <option value="">— select client —</option>
                  {suggestion && <option value="__suggest__">✓ Suggested: {suggestion.company_name}</option>}
<option value="__none__">✕ Not in CRM / didn't take up</option>
                  <optgroup label="FSO leads">
                    {fsoClients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                  </optgroup>
                  <optgroup label="All clients">
                    {(clients||[]).filter(c => c.lead_source !== FSO_SOURCE).slice().sort((a,b)=>(a.company_name||'').localeCompare(b.company_name||'')).map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                  </optgroup>
                </select>
              </div>
              <div>
                {!matched ? (
                  t.match_status==='no_match'
                    ? <span style={{fontSize:11,color:'var(--muted)'}}>Not converted</span>
                    : <span style={{fontSize:11,color:'var(--warning)',fontWeight:600}}>Needs match</span>
                ) : placed ? (
                  <span onClick={()=>openClient(matched)} style={{fontSize:11,color:'var(--forest)',fontWeight:700,cursor:'pointer'}}>✓ {fmtShort(clientNetRevenue(matched, coverConfirmations))} earned</span>
                ) : ni ? (
                  <span style={{fontSize:11,color:'var(--danger)'}}>Not interested</span>
                ) : (
                  <span onClick={()=>openClient(matched)} style={{fontSize:11,color:'var(--muted)',cursor:'pointer'}}>In progress</span>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* IMPORT MODAL */}
      {showImport && (
        <div className="overlay" onClick={e=>e.target===e.currentTarget&&setShowImport(false)}>
          <div className="modal" style={{maxWidth:700}} onClick={e=>e.stopPropagation()}>
            <div className="modal-head">
              <div><div className="modal-title">Import FSO Statement</div><div style={{fontSize:12,color:'var(--muted)',marginTop:2}}>Copy the statement page and paste below — deposits and lead costs are detected automatically</div></div>
              <button className="modal-close" onClick={()=>setShowImport(false)}>✕</button>
            </div>
            <div className="modal-body">
              <textarea className="form-input" rows={8} style={{fontFamily:'monospace',fontSize:12}} placeholder="Paste the whole statement here..." value={pasteText} onChange={e=>setPasteText(e.target.value)} />
              <div style={{display:'flex',gap:8,marginTop:12}}>
                <button className="btn btn-secondary" onClick={()=>{setPasteText('');setParsed([])}}>Clear</button>
                <button className="btn btn-primary" onClick={handleParse} disabled={!pasteText.trim()}>Parse</button>
              </div>

              {parsed.length > 0 && (
                <div style={{marginTop:18}}>
                  <div style={{fontSize:12,fontWeight:700,marginBottom:8}}>
                    Detected {parsed.length} rows — {parsed.filter(r=>r.type==='lead').length} leads, {parsed.filter(r=>r.type==='deposit').length} deposits
                  </div>
                  <div style={{maxHeight:240,overflowY:'auto',border:'1px solid var(--border)',borderRadius:10}}>
                    {parsed.map((r,i)=>(
                      <div key={i} style={{display:'grid',gridTemplateColumns:'80px 1fr 80px',gap:8,padding:'7px 12px',borderBottom:'1px solid var(--border)',fontSize:12,alignItems:'center'}}>
                        <span style={{color:'var(--muted)'}}>{new Date(r.txn_date).toLocaleDateString('en-AU',{day:'numeric',month:'short'})}</span>
                        <span>{r.lead_name || r.description}</span>
                        <span style={{textAlign:'right',fontWeight:600,color:r.debit>0?'var(--danger)':'var(--forest)'}}>{r.debit>0?'-'+fmtMoney(r.debit):'+'+fmtMoney(r.credit)}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:14}}>
                    <button className="btn btn-secondary" onClick={()=>setParsed([])}>Back</button>
                    <button className="btn btn-orange" onClick={handleImport} disabled={importing}>{importing?'Importing...':`Import ${parsed.length} rows`}</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
